
import React, { useState } from 'react'
import { languageController } from '../../../../controllers/language/languageController';
import { getOrdinalNum } from '../../../../utils/helpers';
import { Svg } from '../../../1_Atoms/Svg/Svg';

// Sort days by today's day
function sortDays(days) {
  const dayOfWeek = new Date().getDay() - 1; // Sunday = -1, Monday = 0, ...
  return days.slice(dayOfWeek).concat(days.slice(0, dayOfWeek));
}

// Check if closed (en/fr)
function checkClosed(val) {
  return (val === 0 || val === '0')
}

// Convert time integer XXX or XXXX to X:XX or XX:XX string.
function formatTime(timeNum) {
  // If it's closed time, just return it.
  if (checkClosed(timeNum)) return timeNum;

  const timeNumStr = timeNum.toString();
  const pos = timeNumStr.length === 3 ? 1 : 2;

  return [timeNumStr.slice(0, pos), ':', timeNumStr.slice(pos)].join('');
}

// Get AM/PM label.
const getHourLabel = function (hours) {
  if (!hours) return ''
  return hours >= 12 ? 'pm' : 'am';
}

// Generate time label from 1800 to 6:00pm, 18:00pm, etc.
const generateTimeLabel = (timeNum, is12HFormat = false, hasSpace = false) => {
  if (!timeNum) return '';

  // Convert XXXX/XXX to XX:XX/X:XX.
  let formattedTime = formatTime(timeNum)

  const hourStrPos = formattedTime.indexOf(':');
  const hours = Number(formattedTime.substring(0, hourStrPos));

  // Convert 24H format to 12H format.
  if (is12HFormat) {
    const hoursIn12H = (hours % 12) || 12;
    formattedTime = [hoursIn12H, formattedTime.substring(hourStrPos)].join('');
  }

  return [
    formattedTime,
    hasSpace ? ' ' : '',
    is12HFormat ? getHourLabel(hours) : ''
  ].join('');
}

// TODO: Sanity checks and lang control in FR and EN.
function generateOpenStateLabel(startTimeHour, closeTimeHour, days, is12HFormat = true) {
  const currentTime = new Date();
  const isClosedToday = (checkClosed(startTimeHour) || checkClosed(closeTimeHour));
  const tomorrowOpen = days[1] ? days[1].open : 0;
  const startTime = isClosedToday ? null : new Date(`${new Date().toDateString()} ${formatTime(startTimeHour)}`);
  const closeTime = isClosedToday ? null : new Date(`${new Date().toDateString()} ${formatTime(closeTimeHour)}`);

  // Opened today and currentTime is before startTime
  if (!isClosedToday && currentTime < startTime) {
    return `Closed. Opens at ${generateTimeLabel(startTimeHour, is12HFormat, true)}`;
  }
  // Opened today and currentTime is after startTime and before closeTime
  else if (
    !isClosedToday &&
    (startTime <= currentTime && currentTime < closeTime)
  ) {
    return `Open now until ${generateTimeLabel(closeTimeHour, is12HFormat, true)}`;
  }
  // Opened today AND currentTime is after closeTime AND tomorrow is opened
  //  OR
  // Closed today AND tomorrow is opened
  else if (
    (!isClosedToday && currentTime >= closeTime && !checkClosed(tomorrowOpen)) ||
    (isClosedToday && !checkClosed(tomorrowOpen))
  ) {
    return `Closed. Opens tomorrow at ${generateTimeLabel(tomorrowOpen, is12HFormat, true)}`
  }
  // Otherwise, get the next opening day
  else {
    const nextDay = days.find((day, index) => (index !== 0 && !checkClosed(day.open)));

    if (!nextDay) {
      return 'Closed. Next open day is unknown' // TODO: tmp text.
    }

    const diff = days.indexOf(nextDay)
    const nextDayObj = new Date();
    nextDayObj.setDate(new Date().getDate() + diff);
    return `Closed. Opens on ${nextDayObj.getDate()}${getOrdinalNum(nextDayObj.getDate())} at ${generateTimeLabel(nextDay.open, is12HFormat, true)}`
  }
}

function Hours({ days, label, isInitOpened, ctaLink, isNoDrawer, is24HFormat }) {
  const [isHoursOpened, setIsHoursOpened] = useState(isInitOpened)
  const sortedDays = sortDays(days)

  return (
    <div className={`mz-dealer-card__hour ${isNoDrawer ? 'mz-dealer-card__hour__no-drawer' : ''}`}>
      <button
        className="reset font-base mz-dealer-card__hour-panel"
        aria-expanded={isHoursOpened}
        onClick={() => {
          if (!isNoDrawer) setIsHoursOpened(!isHoursOpened)
        }}
      >
        <span className="font-small upper medium">{label}</span>
        <span className="label label--tight text--grey">{generateOpenStateLabel(sortedDays[0]['open'], sortedDays[0]['closed'], sortedDays, !is24HFormat)}</span>
        <Svg
          size={0.75}
          colour="grey"
          type="chevron_small_down"
        />
      </button>
      <ul className="mz-dealer-card__hour-content reset" aria-hidden={!isHoursOpened}>
        {
          sortedDays.map(({ day, open, closed }, index) => {
            const isClosedToday = checkClosed(open) || checkClosed(closed);
            return (
              <li className="font-small" key={index}>
                <span className={index === 0 ? 'strong' : 'medium'}>{day}</span>
                <span className={`${index === 0 ? 'strong' : 'regular'} ${!isClosedToday ? 'upper' : ''}`}>
                  {
                    isClosedToday ? (
                      languageController.getCopy('findDealer', 'closed')
                    ) : (
                        `${generateTimeLabel(open, !is24HFormat)} - ${generateTimeLabel(closed, !is24HFormat)}`
                      )
                  }
                </span>
              </li>
            )
          })
        }
        {ctaLink && (
          <li className="mz-dealer-card__hour-cta font-small">
            {ctaLink}
          </li>
        )}
      </ul>
    </div>
  )
}

Hours.defaultProps = {
  isOpened: false,
  isNoDrawer: false,
  is24HFormat: false,
}

export default Hours